import { useState, useRef, useEffect, useCallback } from 'react'
import axios from 'axios'

import { isDebug } from '../App.config'
import { retrieveParamByName, getFromSession, storeInSession } from '../Utils'
import { useLMSContext } from '../contexts/LMSContext'

/** 
 * @category Content Controller
 * @typedef {Object} CustomLaunchData 
 * Defines the shape of the Content Controller's Custom Launch Data retrieved
 * by the {@link fetchCustomLaunchData}() function. It contains a single parameter
 * with a string representation of the Content Controller's Custom Launch Data
 * @property {string} custom_param For our uses, it contains the string representation 
 * of the {@link CourseConfig}
 */
type CustomLaunchData = {
    custom_param: string
}

/** 
 * @description Retrieves the URL string needed from the parent window's query 
 * string to fetch the course's Custom Launch Data. The fetch URL is found in 
 * the 'CustomParams' parameter of the query string. 
 * 
 * This URL must be used to fetch the data as soon as possible as it may expire.
 * @category Content Controller
 * @returns {(string | null)} - Returns a URL string or null if the 'CustomParams' 
 * parameter cannot be found in the query string.
 */
const getCustomParamsURL = (): string | null => retrieveParamByName('CustomParams', window.parent.location.href)

/**
 * @description 
 * Retrieves the course's "Custom Launch Data" in the Content Controller.
 * 
 * If it is not found, a null is returned.
 * @category Content Controller
 * @returns {(Promise<CustomLaunchData | null>)}
 * @requires module:axios
 */
const fetchCustomLaunchData = async (): Promise<CustomLaunchData | null> => {
    isDebug && console.log('Fetching Custom Launch Data...')

    const url = getCustomParamsURL()
    if (!url) {
        isDebug && console.warn('Custom Launch Data URL not found in query string.')
        return null
    }
    return await axios.get(url)
        .then(response => {
            return response.data.custom_param ? response.data as CustomLaunchData : null
        })
        .catch(err => {
            console.error(`Error retrieving Custom Launch Data: ${err}`)
            return null
        })
}

/**
 * @description 
 * This React Hook fetches the {@link CustomLaunchData} object from the Content Controller
 * only once and saves it in session storage and state.
 * 
 * The Custom Launch Data must be configured as a single JSON string in the Content Controller. 
 * 
 * The hook returns a 'isLoaded' boolean and a getValue() function to retrieve a specific value 
 * from the Custom Launch Data.
 * @category Content Controller
 * @example <caption>Using it to get a CourseConfig setting.</caption>
 * import useCustomLaunchData from 'useCustomLaunchData'
 * ...
 * const {isLoaded, getValue } = useCustomLaunchData()
 * 
 * useEffect(() => {
 *     if (!isLoaded) return
 * 
 *     const completeOnLevel = getValue(CourseConfigSetting.CompleteOnLevel)
 *     ...  
 * }, [isLoaded, getValue])
 * 
 * 
 * @returns {object} 
 */
const useCustomLaunchData = () => {
    const { lmsState: { isLMSAvailable } } = useLMSContext()
    const [data, setData] = useState<CustomLaunchData | null>(getFromSession('CustomLaunchData') === ''
        ? null
        : JSON.parse(getFromSession('CustomLaunchData')) as CustomLaunchData)

    const fetched = useRef(false)

    useEffect(() => {
        if (!isLMSAvailable || fetched.current || data) return
        fetched.current = true
        const fetchData = async () => {
            const customLaunchData = await fetchCustomLaunchData()
            isDebug && console.log(`Custom Launch Data received:`, customLaunchData)
            if (!customLaunchData) return
            storeInSession('CustomLaunchData', JSON.stringify(customLaunchData))
            setData(customLaunchData)
        }
        fetchData()
    }, [isLMSAvailable, data])


    /**
     * @description Retrieves a value from the Custom Launch Data.     
     * @param {string} key The name of the key
     * @returns {string} The value of the provided key.
     */
    const getValue = useCallback((key: string): string => {
        try {
            if (!data || !data.custom_param)
                throw new Error(`Custom Launch Data is empty. Cannot get non-existent key '${key}'`)

            try {
                const json = JSON.parse(data.custom_param)
                if ((key in json) === false)
                    return ''

                const val = json[key]
                isDebug && console.log(`'${key}' value is '${val as string}'`)
                if (!val && val !== false) isDebug && console.error(`Value in key '${key}' is empty.`)
                return val as string || ''
            }
            catch (e) {
                isDebug && console.error('Custom Launch Data is not a JSON object.', e)
                return ''
            }
        }
        catch (error) {
            console.error(error)
            return ''
        }
    }, [data])

    return { isLoaded: !!data, getValue }
}
export default useCustomLaunchData